import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DashboardQuickActionsSuccess, DashboardTaskCode } from '~/services/api/generated'
import { useApiClient } from '~/services/api'
import { useLogger } from '~/composables/useLogger'
import { resolveDashboardTaskDisplay } from '~/composables/resolveDashboardTaskDisplay'
import { useDashboardQuickActionsStore } from '~/stores/useDashboardQuickActionsStore'
import { useUserStore } from '~/stores/useUserStore'
import { isSafeRedirectPath } from '~/utils/isSafeRedirectPath'
import { identityGeneration } from '~/utils/identityGeneration'
import type { DashboardQuickAction } from './DashboardQuickAction'
import type { DashboardQuickActionGroup } from './DashboardQuickActionGroup'
import type { DashboardQuickActionPriority } from './DashboardQuickActionPriority'
import type { DashboardQuickActionScope } from './DashboardQuickActionScope'

type QuickActionsData = DashboardQuickActionsSuccess['data']
type QuickActionRecord = QuickActionsData['recommended_tasks']['required'][number]

const PRIORITY_BY_GROUP: Record<DashboardQuickActionGroup, DashboardQuickActionPriority> = {
  completed: 'completed',
  important: 'high',
  required: 'critical',
  suggested: 'suggested'
}

const VERIFY_PHONE_ROUTE = '/verify-phone'
const PROFILE_ROUTE = '/dashboard/settings/profile'

const safeRoute = (candidate: string): string =>
  isSafeRedirectPath(candidate) ? candidate : '/dashboard'

const recordRoute = (record: QuickActionRecord): string =>
  safeRoute(record.meta.next_route ?? record.route)

// A store-setup task names its store in its params; no brand and no next step are both real states.
const taskScope = (task: QuickActionRecord): DashboardQuickActionScope => {
  const { params } = task.task_code
  const { brand_id: brandId, brand_name: brandName, next_step: nextStep } = task.meta
  const storeId = task.meta.store_id
  const isStoreSetupTask = task.task_code.code === 'COMPLETE_STORE_SETUP'
  if (!isStoreSetupTask || params === undefined || storeId === undefined) return { kind: 'account' }

  return {
    kind: 'store',
    store: {
      brand:
        brandId === undefined || brandName === undefined
          ? { kind: 'unbranded' }
          : { id: brandId, kind: 'brand', name: brandName },
      completedCheckpoints: task.count ?? 0,
      nextStep: nextStep === undefined ? { kind: 'none' } : { code: nextStep, kind: 'pending' },
      storeId,
      storeName: params.store_name,
      totalCheckpoints: task.total ?? 0
    }
  }
}

const taskEntityId = (task: QuickActionRecord): number | string =>
  task.meta.store_id ?? task.meta.affected_brands?.[0]?.brand_id ?? 'default'

export const useDashboardQuickActions = () => {
  const logger = useLogger('useDashboardQuickActions')
  const { dashboard: dashboardApi } = useApiClient()
  const { t } = useI18n()
  const store = useDashboardQuickActionsStore()
  const { data, isLoaded, isLoading } = storeToRefs(store)

  const allRecords = computed<QuickActionRecord[]>(() => {
    const groups = data.value.recommended_tasks

    return [
      ...groups.required,
      ...groups.important,
      ...groups.suggested,
      ...(groups.completed ?? [])
    ]
  })

  // A locked task leads where the task locking it leads.
  const routeOfTask = (code: DashboardTaskCode): string => {
    const lockingTask = allRecords.value.find((record) => record.task_code.code === code)
    return lockingTask === undefined ? '/dashboard' : recordRoute(lockingTask)
  }

  // Multi-factor sign-in is one task: verify the phone first, then come back to turn on two-factor.
  const taskRoute = (task: QuickActionRecord): string => {
    if (task.meta.locked && task.meta.locked_by !== undefined) {
      return routeOfTask(task.meta.locked_by)
    }
    const isMfaTask = task.task_code.code === 'ENABLE_MFA'
    if (isMfaTask) {
      const userStore = useUserStore()
      if (!userStore.hasPhone) return PROFILE_ROUTE
      if (!userStore.isPhoneVerified) {
        return `${VERIFY_PHONE_ROUTE}?returnUrl=${encodeURIComponent(recordRoute(task))}`
      }
    }

    return recordRoute(task)
  }

  const normalizeTask = (
    task: QuickActionRecord,
    group: DashboardQuickActionGroup,
    index: number
  ): DashboardQuickAction => {
    const { code } = task.task_code
    const isLocked = task.meta.locked
    const display = resolveDashboardTaskDisplay(task.task_code, isLocked, t)

    return {
      code,
      description: display.description,
      group,
      id: `${group}-${code}-${taskEntityId(task)}-${index}`,
      isLocked,
      opensPartnerAgreement: code === 'ACCEPT_MSA' && !isLocked && !task.completed,
      priority: PRIORITY_BY_GROUP[group],
      route: taskRoute(task),
      scope: taskScope(task),
      title: display.title
    }
  }

  // The server orders every group; the dashboard renders that order as is.
  const tasks = computed<DashboardQuickAction[]>(() => {
    const recommendedTasks = data.value.recommended_tasks

    return [
      ...recommendedTasks.required.map((task, index) => normalizeTask(task, 'required', index)),
      ...recommendedTasks.important.map((task, index) => normalizeTask(task, 'important', index)),
      ...recommendedTasks.suggested.map((task, index) => normalizeTask(task, 'suggested', index)),
      ...(recommendedTasks.completed ?? []).map((task, index) =>
        normalizeTask(task, 'completed', index)
      )
    ]
  })

  const urgentTasks = computed(() =>
    tasks.value.filter((task) => task.priority === 'critical' || task.priority === 'high')
  )
  const completion = computed(() => (isLoaded.value ? data.value.completion : null))

  const load = async (force = false): Promise<void> => {
    if (isLoading.value || (isLoaded.value && !force)) return

    const generation = identityGeneration.current()
    isLoading.value = true
    try {
      const response = await dashboardApi().getQuickActions()
      if (!identityGeneration.isCurrent(generation)) return

      data.value = response.data
      isLoaded.value = true
    } catch (error) {
      if (!identityGeneration.isCurrent(generation)) return
      logger.error('load', error)
    } finally {
      if (identityGeneration.isCurrent(generation)) isLoading.value = false
    }
  }

  return { completion, data, isLoaded, isLoading, load, reset: store.reset, tasks, urgentTasks }
}
